import React, { useCallback, useEffect, useState } from "react";
import { formatRelativeTime } from "../../utils/ledgerHelpers";
import { agreementTemplates, commonSections, governingLawVars } from "../../utils/agreementTemplates";
import SignaturePad from "./SignaturePad";
import { screenFields } from "../../utils/screen";

const PKG_ICONS = {
  FOODLINK: "/FoodShare.png",
  BLOODSHARE: "/BloodShare.png",
  BILLBRIDGE: "/BillPayShare.png",
  RESCUEBIDS: "/RescueBids.jpg",
  DIGITHER: "/DigitHer.png",
  EDUCATIONX: "/EducationX.webp",
};

/** Map backend packageName → agreementTemplates key (same fallbacks as mobile) */
function templateKeyForPackage(packageName) {
  const raw = (packageName || "").toLowerCase();
  if (raw === "foodlink" || raw === "foodshare" || raw === "food") return "foodshare";
  if (raw === "bloodshare" || raw === "blood") return "bloodshare";
  if (raw === "educationx" || raw === "education") return "educationx";
  if (raw === "digither") return "digither";
  if (raw === "billbridge" || raw === "rescuebids") return "foodshare";
  return agreementTemplates[raw] ? raw : "foodshare";
}

function needTypeFromPackage(packageName) {
  switch (packageName) {
    case "FOODLINK": return "food";
    case "BLOODSHARE": return "blood";
    case "BILLBRIDGE": return "billbridge";
    case "RESCUEBIDS": return "rescuebids";
    case "DIGITHER": return "digither";
    case "EDUCATIONX": return "educationx";
    default: return "";
  }
}

/** Normalize backend needType filter to package-derived type (MBTS parity) */
function normalizeNeedTypeFilter(filter) {
  if (!filter) return "";
  const map = {
    bill: "billbridge",
    rescue: "rescuebids",
    education: "educationx",
  };
  return map[filter] || filter;
}

function fillPlaceholders(text, vars) {
  if (!text) return "";
  return text
    .replace(/\{date\}/g, vars.date || "")
    .replace(/\{requesterName\}/g, vars.requesterName || "")
    .replace(/\{providerName\}/g, vars.providerName || "")
    .replace(/\{requesterAddress\}/g, vars.requesterAddress || "")
    .replace(/\{providerAddress\}/g, vars.providerAddress || "")
    .replace(/\{deliveryTime\}/g, vars.deliveryTime || "")
    .replace(/\{bloodType\}/g, vars.bloodType || "Not Specified")
    .replace(/\{governingCity\}/g, vars.governingCity || "the Requester's jurisdiction")
    .replace(/\{governingRegion\}/g, vars.governingRegion || "applicable state or territory");
}

// RescueBids gratuity (BID_GRATUITY_ENABLED on the back end). The server sends
// gratuity, needAmount (null when the need has no amount) and total on a bid.
const GRATUITY_PATTERN = /^\d+(\.\d{1,2})?$/;
const money = (n) => `${(Number(n) || 0).toFixed(2)}`;
function gratuityLine(item) {
  if (!item || item.gratuity === undefined) return "";
  const gratuity = Number(item.gratuity) || 0;
  if (typeof item.needAmount === "number") {
    const total = typeof item.total === "number" ? item.total : item.needAmount + gratuity;
    return `${money(item.needAmount)} + ${money(gratuity)} gratuity = ${money(total)}`;
  }
  return `Gratuity: ${money(gratuity)}`;
}
// Shown for RescueBids bids, and for any bid that carries a gratuity.
function bidGratuityLine(bid, need) {
  const isRescue = (bid?.packageName || need?.packageName) === "RESCUEBIDS";
  return isRescue || Number(bid?.gratuity) > 0 ? gratuityLine(bid) : "";
}

function bidderName(bid) {
  const first = bid.firstName || bid.userId?.firstName || "";
  const last = bid.lastName || bid.userId?.lastName || "";
  return `${first} ${last}`.trim() || "Community member";
}

function AgreementModal({ open, onClose, contract, currentUser }) {
  const [signatures, setSignatures] = useState([]);
  const [agreementHash, setAgreementHash] = useState("");
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState("");

  const contractId = contract?.contractId || null;
  const party = contract?.party || null;
  const token = currentUser?.token;
  const userId = currentUser?._id;

  const key = templateKeyForPackage(contract?.packageName);
  const template = agreementTemplates[key] || agreementTemplates.foodshare;
  const dateStr = contract?.dateOfContract
    ? new Date(contract.dateOfContract).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];
  const deliveryStr = contract?.deliveryTime
    ? new Date(contract.deliveryTime).toISOString().split("T")[0]
    : dateStr;
  const gov = governingLawVars(currentUser, contract || {});
  const vars = {
    date: dateStr,
    requesterName: contract?.requesterName,
    providerName: contract?.providerName,
    requesterAddress: contract?.requesterAddress,
    providerAddress: contract?.providerAddress,
    deliveryTime: deliveryStr,
    bloodType: contract?.bloodType,
    governingCity: gov.governingCity,
    governingRegion: gov.governingRegion,
  };

  const sections = [];
  if (template.parties?.description) sections.push(["PARTIES", template.parties.description]);
  if (template.agreement) sections.push(["AGREEMENT", template.agreement]);
  if (template.purpose) sections.push(["PURPOSE", template.purpose]);
  if (template.valueExchange) sections.push(["VALUE IN THE EXCHANGE", template.valueExchange]);
  if (template.eligibility) sections.push(["ELIGIBILITY", template.eligibility]);
  if (template.requesterRepresentation) sections.push(["REQUESTER REPRESENTATION", template.requesterRepresentation]);
  if (template.providerRepresentation) sections.push(["PROVIDER REPRESENTATION", template.providerRepresentation]);
  if (template.deliveryPlan) sections.push(["DELIVERY PLAN", template.deliveryPlan]);
  if (template.bloodTypeCompatibility) sections.push(["BLOOD TYPE COMPATIBILITY", template.bloodTypeCompatibility]);
  if (template.donorMatchProtocol) sections.push(["DONOR MATCH PROTOCOL", template.donorMatchProtocol]);
  if (template.voluntaryParticipation) sections.push(["VOLUNTARY PARTICIPATION", template.voluntaryParticipation]);
  if (template.disclaimers) sections.push(["DISCLAIMERS", template.disclaimers]);
  if (template.emergencyUse) sections.push(["EMERGENCY USE", template.emergencyUse]);
  if (template.indemnity) sections.push(["INDEMNIFICATION", template.indemnity]);
  if (template.gratuity) sections.push(["GRATUITY", template.gratuity]);
  if (template.termination) sections.push(["TERMINATION", template.termination]);
  if (template.supplemental) sections.push(["SUPPLEMENTAL", template.supplemental]);
  if (template.consent) sections.push(["CONSENT", template.consent]);
  if (template.default) sections.push(["DEFAULT", template.default]);
  if (commonSections.indemnity) sections.push(["INDEMNITY", commonSections.indemnity]);
  if (commonSections.governingLaw) sections.push(["GOVERNING LAW", commonSections.governingLaw]);
  if (commonSections.severability) sections.push(["SEVERABILITY", commonSections.severability]);
  if (commonSections.entireAgreement) sections.push(["ENTIRE AGREEMENT", commonSections.entireAgreement]);
  if (commonSections.amendments) sections.push(["AMENDMENTS", commonSections.amendments]);
  if (commonSections.notices) sections.push(["NOTICES", commonSections.notices]);

  // Integrity hash: SHA-256 of the agreement text exactly as rendered to the signer
  const agreementText = [template.title, template.subtitle, ...sections.map(([t, b]) => `${t}\n${fillPlaceholders(b, vars)}`)].join("\n\n");

  useEffect(() => {
    if (!open || !agreementText || !window.crypto?.subtle) return;
    let alive = true;
    crypto.subtle.digest("SHA-256", new TextEncoder().encode(agreementText))
      .then((buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join(""))
      .then((hex) => { if (alive) setAgreementHash(hex); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, agreementText]);

  useEffect(() => {
    if (!open || !contractId) { setSignatures([]); return; }
    setSignError("");
    fetch(`/api/bids/contract/${contractId}/signature`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : { data: null }))
      .then((d) => {
        const list = Array.isArray(d?.data?.signatures) ? d.data.signatures : Array.isArray(d?.data) ? d.data : [];
        setSignatures(list);
      })
      .catch(() => setSignatures([]));
  }, [open, contractId, token]);

  if (!open || !contract) return null;

  async function handleSign({ imageBase64, name, method }) {
    if (!contractId) return;
    const nameCheck = await screenFields([["legalName", name]]);
    if (!nameCheck.ok) { setSignError(nameCheck.message); return; }
    setSigning(true);
    setSignError("");
    try {
      const res = await fetch(`/api/bids/contract/${contractId}/signature`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ party, name, imageBase64, method, timestamp: new Date().toISOString(), agreementHash, userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Signature could not be saved — backend signature endpoint is not live yet.");
      }
      const saved = data?.data?.signatures || data?.signatures;
      if (Array.isArray(saved)) setSignatures(saved);
      else setSignatures((p) => [...p.filter((s) => s.party !== party), { party, name, imageBase64, timestamp: new Date().toISOString(), agreementHash }]);
    } catch (err) {
      setSignError(err.message || "Could not save signature");
    } finally {
      setSigning(false);
    }
  }

  const sigFor = (p) => signatures.find((s) => s.party === p);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9740, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 12, width: "min(560px,100%)", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #e0e0e0" }}>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: "#00B8D4", fontWeight: 700, cursor: "pointer" }}>← Back</button>
          <span style={{ marginLeft: 10, fontSize: 18, fontWeight: 700, color: "#00B8D4" }}>Smart Contract</span>
        </div>
        <div style={{ overflow: "auto", padding: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, textAlign: "center", marginBottom: 6 }}>{template.title}</div>
          <div style={{ fontSize: 13, color: "#666", textAlign: "center", marginBottom: 18, whiteSpace: "pre-line" }}>{template.subtitle}</div>
          {contract?.gratuityLine && (
            <div style={{ marginBottom: 16, padding: "10px 12px", background: "#F1F8E9", borderRadius: 8, fontWeight: 600, color: "#2e7d32", fontSize: 14 }}>
              Gratuity on this bid: {contract.gratuityLine}
            </div>
          )}
          {sections.map(([title, body]) => (
            <div key={title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#00B8D4", marginBottom: 6 }}>{title}</div>
              <div style={{ fontSize: 13, color: "#333", lineHeight: 1.5, whiteSpace: "pre-line" }}>{fillPlaceholders(body, vars)}</div>
            </div>
          ))}
          {/* Consent renders only real captured signatures — never a hardcoded name */}
          <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid #eee" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#00B8D4", marginBottom: 8 }}>SIGNATURES</div>
            {["requester", "provider"].map((p) => {
              const sig = sigFor(p);
              const label = p === "requester" ? contract.requesterName : contract.providerName;
              return (
                <div key={p} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                    {p === "requester" ? "Requester" : "Provider"}: {label}
                  </div>
                  {sig ? (
                    <div>
                      {sig.imageBase64 && (
                        <img src={sig.imageBase64} alt={`Signature of ${sig.name}`}
                          style={{ height: 48, border: "1px solid #eee", borderRadius: 6, background: "#fff", display: "block", marginBottom: 4 }} />
                      )}
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Signed by {sig.name}</div>
                      {sig.timestamp && <div style={{ fontSize: 11, color: "#888" }}>{new Date(sig.timestamp).toLocaleString()}</div>}
                    </div>
                  ) : p === party && contractId ? (
                    <SignaturePad
                      defaultName={currentUser?.name || label || ""}
                      onSign={handleSign}
                      signing={signing}
                      error={signError}
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: "#B8860B", fontStyle: "italic" }}>Awaiting signature</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ open, onClose, contractId, providerName, gratuityLine: bidGratuity, currentUser, authHeader, onSubmitted }) {
  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState("");
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const userId = currentUser?._id;
  const token = currentUser?.token;

  useEffect(() => {
    if (!open || !contractId || !userId) return;
    setError("");
    setSelected(null);
    setReview("");
    setExisting(null);
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    fetch(`/api/bids/contract/${contractId}/review${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.data) {
          setExisting(data.data);
          setSelected(data.data.experience || null);
          setReview(data.data.review || "");
        }
      })
      .catch(() => {});
  }, [open, contractId, userId, token]);

  if (!open || !contractId) return null;

  async function submit(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (existing) { setError("You have already submitted a review"); return; }
    if (!selected) { setError("Please select your experience"); return; }
    if (!review.trim()) { setError("Please write your review"); return; }
    const reviewCheck = await screenFields([["review", review]]);
    if (!reviewCheck.ok) { setError(reviewCheck.message); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/bids/contract/${contractId}/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ review: review.trim(), experience: selected, userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to submit review");
      if (onSubmitted) onSubmitted(data?.message || "Review submitted successfully");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to submit review");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 99990, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12, pointerEvents: "auto" }}
      onClick={onClose}>
      <form onSubmit={submit}
        style={{ background: "#fff", borderRadius: 12, width: "min(420px,100%)", padding: 20, pointerEvents: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: "#00B8D4", fontWeight: 700, cursor: "pointer" }}>← Back</button>
          <span style={{ marginLeft: 10, fontSize: 18, fontWeight: 700, color: "#00B8D4" }}>Review Contract</span>
        </div>
        {bidGratuity && (
          <p style={{ textAlign: "center", fontSize: 14, fontWeight: 600, color: "#2e7d32", margin: "0 0 10px" }}>{bidGratuity}</p>
        )}
        <p style={{ textAlign: "center", fontSize: 15, fontWeight: 500, marginBottom: 20 }}>
          How was your experience with {providerName}?
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 40, marginBottom: 20 }}>
          {/* Emoji glyphs ignore CSS `color` in Chrome — selection must use filter/background */}
          <button type="button" disabled={!!existing} onClick={(e) => { e.stopPropagation(); setSelected("like"); }}
            style={{ border: "none", background: selected === "like" ? "rgba(0,184,212,0.15)" : "transparent", borderRadius: 12, padding: "6px 14px", cursor: existing ? "default" : "pointer", textAlign: "center", pointerEvents: "auto" }}>
            <div style={{ fontSize: 48, filter: selected === "like" ? "none" : "grayscale(100%) opacity(0.45)", transition: "filter 0.15s" }}>👍</div>
            <div style={{ color: "#00B8D4", fontWeight: selected === "like" ? 700 : 600, marginTop: 4 }}>Like</div>
          </button>
          <button type="button" disabled={!!existing} onClick={(e) => { e.stopPropagation(); setSelected("dislike"); }}
            style={{ border: "none", background: selected === "dislike" ? "rgba(0,184,212,0.15)" : "transparent", borderRadius: 12, padding: "6px 14px", cursor: existing ? "default" : "pointer", textAlign: "center", pointerEvents: "auto" }}>
            <div style={{ fontSize: 48, filter: selected === "dislike" ? "none" : "grayscale(100%) opacity(0.45)", transition: "filter 0.15s" }}>👎</div>
            <div style={{ color: "#00B8D4", fontWeight: selected === "dislike" ? 700 : 600, marginTop: 4 }}>Dislike</div>
          </button>
        </div>
        <textarea
          placeholder="Write your review"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          disabled={!!existing}
          rows={4}
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #E0E0E0", borderRadius: 6, padding: 10, marginBottom: 12, resize: "vertical" }}
        />
        {error && <div style={{ color: "#c00", fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <button type="submit" disabled={loading || !!existing}
          style={{ width: "100%", background: existing ? "#ccc" : "#00B8D4", color: "#fff", border: "none", borderRadius: 6, padding: "12px 0", fontWeight: 600, cursor: existing ? "default" : "pointer" }}>
          {existing ? "Review Submitted" : loading ? "Submitting…" : "Submit Review"}
        </button>
      </form>
    </div>
  );
}

export default function NeedsScreenWeb({ open, mode, needTypeFilter, currentUser, onClose, onBidPosted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bidItem, setBidItem] = useState(null);
  const [bidMessage, setBidMessage] = useState("");
  // RescueBids: the bidder's optional gratuity (empty means 0).
  const [bidGratuity, setBidGratuity] = useState("");
  const [submittingBid, setSubmittingBid] = useState(false);
  const [expandedNeed, setExpandedNeed] = useState(null);
  const [bidsByNeed, setBidsByNeed] = useState({});
  const [loadingBids, setLoadingBids] = useState({});
  const [acceptingId, setAcceptingId] = useState(null);
  const [contractView, setContractView] = useState(null);
  const [reviewView, setReviewView] = useState(null);
  const [myBidNeedIds, setMyBidNeedIds] = useState(() => new Set());
  const [myBidByNeed, setMyBidByNeed] = useState({});

  const authHeader = currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {};

  const loadMyBidNeedIds = useCallback(async () => {
    if (!currentUser?._id || !currentUser?.token) return;
    try {
      const res = await fetch(`/api/bids/user/${currentUser._id}`, {
        headers: { Authorization: `Bearer ${currentUser.token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const bids = Array.isArray(data?.data) ? data.data : [];
      const byNeed = {};
      for (const b of bids) {
        const need = b.needId;
        const id = typeof need === "object" ? need?._id : need;
        if (id) byNeed[id] = b;
      }
      setMyBidNeedIds(new Set(Object.keys(byNeed)));
      setMyBidByNeed(byNeed);
    } catch {
      // keep local state if fetch fails
    }
  }, [currentUser?._id, currentUser?.token]);

  const load = useCallback(async () => {
    if (!open || !currentUser?._id) return;
    setLoading(true);
    setError("");
    try {
      if (mode === "myNeeds") {
        const res = await fetch(`/api/xshares/${currentUser._id}`, { headers: authHeader });
        const data = await res.json();
        setItems(Array.isArray(data) ? data : data?.data || []);
      } else {
        const res = await fetch("/api/xshares/need-type", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify({
            userData: {
              ...currentUser,
              user: { packages: currentUser.package || currentUser.user?.packages || [] },
            },
          }),
        });
        const data = await res.json();
        let list = Array.isArray(data) ? data : data?.data || data?.needTypes || [];
        list = list.filter((item) => item.userId?._id !== currentUser._id);
        if (needTypeFilter) {
          const normalized = normalizeNeedTypeFilter(needTypeFilter);
          list = list.filter((item) => needTypeFromPackage(item.packageName) === normalized);
        }
        setItems(list);
      }
    } catch (e) {
      setError(e.message || "Could not load needs");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [open, mode, needTypeFilter, currentUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (open && mode === "activeNeeds") loadMyBidNeedIds();
  }, [open, mode, loadMyBidNeedIds]);

  async function loadBidsForNeed(needId) {
    if (!needId) return;
    setLoadingBids((p) => ({ ...p, [needId]: true }));
    try {
      const res = await fetch(`/api/bids/need/${needId}`, { headers: authHeader });
      const data = await res.json();
      const bids = Array.isArray(data?.data) ? data.data : [];
      setBidsByNeed((p) => ({ ...p, [needId]: bids }));
    } catch (e) {
      setBidsByNeed((p) => ({ ...p, [needId]: [] }));
      setError(e.message || "Could not load bids");
    } finally {
      setLoadingBids((p) => ({ ...p, [needId]: false }));
    }
  }

  async function toggleBidders(needId) {
    if (expandedNeed === needId) {
      setExpandedNeed(null);
      return;
    }
    setExpandedNeed(needId);
    await loadBidsForNeed(needId);
  }

  async function submitBid(e) {
    e.preventDefault();
    if (!bidItem || !bidMessage.trim()) return;
    const bidCheck = await screenFields([["message", bidMessage]]);
    if (!bidCheck.ok) { setError(bidCheck.message); return; }
    const withGratuity = bidItem.packageName === "RESCUEBIDS";
    const gratuityText = bidGratuity.trim();
    if (withGratuity && gratuityText) {
      const numberCheck = await screenFields([["number", gratuityText]]);
      if (!numberCheck.ok) { setError(numberCheck.message); return; }
      if (!GRATUITY_PATTERN.test(gratuityText)) { setError("Please enter a gratuity of 0 or more (for example 5 or 7.50)."); return; }
    }
    setSubmittingBid(true);
    setError("");
    try {
      const res = await fetch("/api/bids/create-bid", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          message: bidMessage.trim(),
          needId: bidItem._id,
          userId: currentUser._id,
          ...(withGratuity ? { gratuity: gratuityText || "0" } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Bid failed");
      setBidItem(null);
      setBidMessage("");
      setBidGratuity("");
      setMyBidNeedIds((prev) => new Set([...prev, bidItem._id]));
      if (onBidPosted) onBidPosted(data?.message || "Bid posted successfully.");
      await load();
    } catch (e) {
      setError(e.message || "Could not post bid");
    } finally {
      setSubmittingBid(false);
    }
  }

  // ProtoPie Session 5 / mobile Bidders.js — accept one bid → smart contract
  async function handleAcceptBid(bid, need) {
    if (!bid?._id) return;
    setAcceptingId(bid._id);
    setError("");
    try {
      const res = await fetch(`/api/bids/accept/${bid._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ needId: need._id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Accept bid failed");
      await loadBidsForNeed(need._id);
      await load();
      if (onBidPosted) {
        onBidPosted(data?.message || "Bid accepted and smart contract created.");
      }
    } catch (e) {
      setError(e.message || "Could not accept bid");
    } finally {
      setAcceptingId(null);
    }
  }

  function openContract(bid, need) {
    const provider = bidderName(bid);
    setContractView({
      packageName: bid.packageName || need.packageName || "FOODLINK",
      requesterName: `${currentUser?.name || currentUser?.firstName || "Requester"}`.trim(),
      providerName: provider,
      requesterAddress: bid.needId?.address || need.address || "Address not specified",
      providerAddress: bid.userId?.homeCity || bid.homeCity || "Address not specified",
      dateOfContract: bid.updatedAt || new Date().toISOString(),
      deliveryTime: need.byWhen || bid.needId?.byWhen || new Date().toISOString(),
      bloodType: need.bloodType,
      contractId: typeof bid.contract === "object" ? bid.contract?._id : bid.contract,
      party: "requester",
      gratuityLine: bidGratuityLine(bid, need),
    });
  }

  // Provider (winning bidder) opens the same contract to review + sign it
  function openContractAsProvider(myBid, need, myContractId) {
    setContractView({
      packageName: myBid.packageName || need.packageName || "FOODLINK",
      requesterName: `${need.userId?.firstName || ""} ${need.userId?.lastName || ""}`.trim() || "Requester",
      providerName: `${currentUser?.name || currentUser?.firstName || "Provider"}`.trim(),
      requesterAddress: need.address || "Address not specified",
      providerAddress: currentUser?.city || "Address not specified",
      dateOfContract: myBid.updatedAt || new Date().toISOString(),
      deliveryTime: need.byWhen || new Date().toISOString(),
      bloodType: need.bloodType,
      contractId: myContractId,
      party: "provider",
    });
  }

  function openReview(bid) {
    setReviewView({
      contractId: typeof bid.contract === "object" ? bid.contract?._id : bid.contract,
      providerName: bidderName(bid),
      gratuityLine: bidGratuityLine(bid),
    });
  }

  if (!open) return null;

  const title = mode === "myNeeds" ? "My Needs" : "Active Needs";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9720, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px" }}
      onClick={onClose}>
      <div style={{ background: "#f9f9f9", borderRadius: "16px", width: "min(560px,100%)", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px", borderBottom: "1px solid #ddd" }}>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: "#00B8D4", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}>← Back</button>
          <span style={{ fontSize: "18px", fontWeight: 700, color: "#00B8D4" }}>{title}</span>
          <button type="button" onClick={load} style={{ marginLeft: "auto", border: "none", background: "transparent", color: "#666", cursor: "pointer", fontSize: "12px" }}>Refresh</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
          {loading && <div style={{ textAlign: "center", padding: "24px", color: "#666" }}>Loading…</div>}
          {error && <div style={{ color: "#c00", fontSize: "13px", marginBottom: "8px" }}>{error}</div>}
          {!loading && items.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px", color: "#888" }}>
              {needTypeFilter ? `No needs found for ${needTypeFilter}` : "No needs found"}
            </div>
          )}
          {items.map((item) => {
            const bids = bidsByNeed[item._id] || [];
            const bidCount = item.bids?.length || bids.length || 0;
            const anyAccepted = bids.some((b) => b.status === "accepted");
            const alreadyBid = myBidNeedIds.has(item._id);
            // Provider side: my bid on this need was accepted → I can review the requester
            const myBid = myBidByNeed[item._id];
            const myContractId = myBid?.status === "accepted"
              ? (typeof myBid.contract === "object" ? myBid.contract?._id : myBid.contract)
              : null;
            return (
              <div key={item._id} style={{ background: "#fff", borderRadius: "12px", padding: "14px", marginBottom: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "8px" }}>
                  <img src={PKG_ICONS[item.packageName] || "/boticon.png"} alt="" style={{ width: "40px", height: "40px", borderRadius: "8px", objectFit: "cover" }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px" }}>
                      {mode === "myNeeds"
                        ? `${currentUser?.name || "You"}`
                        : `${item.userId?.firstName || ""} ${item.userId?.lastName || ""}`.trim() || "Community member"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#888" }}>Posted {formatRelativeTime(item.createdAt)}</div>
                  </div>
                </div>
                {/* Blood group is PHI — backend must target posts only to matching blood-type users. */}
                <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>
                  {item.packageName}
                </div>
                {item.description && <div style={{ fontSize: "13px", color: "#444", marginBottom: "10px" }}>{item.description}</div>}
                {item.address && <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>📍 {item.address}</div>}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {mode === "activeNeeds" ? (
                    <>
                      <button type="button" onClick={() => { setBidItem(item); setBidMessage(""); setBidGratuity(""); }}
                        disabled={alreadyBid}
                        style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: alreadyBid ? "#ccc" : "#00B8D4", color: "#fff", fontWeight: 600, cursor: alreadyBid ? "default" : "pointer", fontSize: "13px" }}>
                        {alreadyBid ? "Bid Posted" : "Post Bid"}
                      </button>
                      {myContractId && (
                        <>
                          <button type="button"
                            onClick={() => openContractAsProvider(myBid, item, myContractId)}
                            style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#ECECEC", color: "#00B8D4", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>
                            View Contract
                          </button>
                          <button type="button"
                            onClick={() => setReviewView({
                              contractId: myContractId,
                              providerName: `${item.userId?.firstName || ""} ${item.userId?.lastName || ""}`.trim() || "Community member",
                            })}
                            style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#ECECEC", color: "#00B8D4", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>
                            Review Contract
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <button type="button" onClick={() => toggleBidders(item._id)}
                      style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#00B8D4", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>
                      {expandedNeed === item._id ? "Hide Bidders" : `View Bidders${bidCount ? ` (${bidCount})` : ""}`}
                    </button>
                  )}
                </div>
                {mode === "myNeeds" && expandedNeed === item._id && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #eee" }}>
                    {loadingBids[item._id] && <div style={{ fontSize: "12px", color: "#888" }}>Loading bidders…</div>}
                    {!loadingBids[item._id] && bids.length === 0 && (
                      <div style={{ fontSize: "12px", color: "#888" }}>No bids yet — share your need and ask your community to bid.</div>
                    )}
                    {bids.map((bid) => {
                      const name = bidderName(bid);
                      const initials = name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
                      const isAccepted = bid.status === "accepted";
                      const disabled = anyAccepted || acceptingId === bid._id;
                      const contractId = typeof bid.contract === "object" ? bid.contract?._id : bid.contract;
                      return (
                        <div key={bid._id} style={{ background: "#fff", border: "1px solid #eee", borderRadius: "10px", padding: "12px", marginBottom: "10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "green", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 }}>{initials}</div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
                                <div style={{ fontSize: 11, color: "#7D7D7D" }}>{formatRelativeTime(bid.createdAt)}</div>
                              </div>
                            </div>
                            {isAccepted && contractId && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <button type="button" onClick={() => openContract(bid, item)}
                                  style={{ background: "#ECECEC", color: "#00B8D4", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer", width: 100 }}>
                                  View Contract
                                </button>
                                <button type="button" onClick={() => openReview(bid)}
                                  style={{ background: "#ECECEC", color: "#00B8D4", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer", width: 100 }}>
                                  Review Contract
                                </button>
                              </div>
                            )}
                          </div>
                          <div style={{ background: "#F0F0F0", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 13, color: "#333" }}>
                            {bid.message}
                            {bidGratuityLine(bid, item) && (
                              <div style={{ marginTop: 6, fontWeight: 600, color: "#2e7d32" }}>{bidGratuityLine(bid, item)}</div>
                            )}
                          </div>
                          <button type="button"
                            onClick={() => handleAcceptBid(bid, item)}
                            disabled={disabled}
                            style={{
                              width: "100%", padding: 10, borderRadius: 8, border: "none",
                              background: isAccepted ? "#4CAF50" : "#00B8D4",
                              color: "#fff", fontWeight: 600, fontSize: 14,
                              cursor: disabled ? "default" : "pointer",
                              opacity: disabled && !isAccepted ? 0.7 : 1,
                            }}>
                            {isAccepted ? "Accepted" : acceptingId === bid._id ? "Accepting…" : "Accept"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {bidItem && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9730, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          onClick={() => setBidItem(null)}>
          <form onSubmit={submitBid}
            style={{ background: "#fff", borderRadius: "12px", padding: "20px", width: "min(400px,100%)", display: "flex", flexDirection: "column", gap: "12px" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700 }}>Post Bid</div>
            <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>
              Same as mobile / ProtoPie: others bid on Active Needs; the need owner Accepts one bid to create the smart contract.
            </p>
            <textarea value={bidMessage} onChange={(e) => setBidMessage(e.target.value)} placeholder="Your message…" rows={4} required
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ccc", resize: "vertical" }} />
            {bidItem.packageName === "RESCUEBIDS" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600 }}>
                Gratuity (optional)
                <input type="text" inputMode="decimal" value={bidGratuity} placeholder="0.00"
                  onChange={(e) => setBidGratuity(e.target.value.replace(/[^0-9.]/g, ""))}
                  style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ccc", fontWeight: 400 }} />
              </label>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setBidItem(null)} style={{ padding: "8px 16px", border: "1px solid #ccc", borderRadius: "8px", background: "#fff", cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={submittingBid} style={{ padding: "8px 16px", border: "none", borderRadius: "8px", background: "#00B8D4", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                {submittingBid ? "Posting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}

      <AgreementModal open={!!contractView} onClose={() => setContractView(null)} contract={contractView} currentUser={currentUser} />
      <ReviewModal
        open={!!reviewView}
        onClose={() => setReviewView(null)}
        contractId={reviewView?.contractId}
        providerName={reviewView?.providerName}
        gratuityLine={reviewView?.gratuityLine}
        currentUser={currentUser}
        onSubmitted={(msg) => { if (onBidPosted) onBidPosted(msg); }}
      />
    </div>
  );
}
